// Theme-mode context — single place that decides whether the app renders in
// dark or light mode. Persists choice to localStorage so it survives reloads.
//
// Wrap the app in <ThemeModeProvider>. Components read mode + toggle via
// `useThemeMode()`; raw colour ramps via `useThemeColors()`.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import {
  createVeraxisTheme, ramps,
  ACCENT, ACCENT_LIGHT, ACCENT_DARK, SECONDARY, SUCCESS, WARN, DANGER,
  SIGNAL, STONE, INK, CREAM,
} from '../theme';

const STORAGE_KEY = 'veraxis.themeMode.v1';

const ThemeModeContext = createContext({
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
});

function readInitialMode() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch { /* localStorage may be blocked */ }
  // Brand defaults to the light "paper" system. A user's explicit toggle is
  // persisted above and wins; OS preference is deliberately NOT consulted so
  // first-run always shows the brand's intended light identity.
  return 'light';
}

export function ThemeModeProvider({ children }) {
  const [mode, setModeState] = useState(readInitialMode);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  // Memoise the MUI theme so deep MUI components don't re-evaluate on every render.
  const theme = useMemo(() => createVeraxisTheme(mode), [mode]);

  // Update the document <meta theme-color> so mobile browsers paint the
  // status bar correctly when mode flips. The `data-theme` attribute mirrors
  // the mode so the CSS custom-property overrides in index.css can switch
  // (raw HTML surfaces that don't go through MUI's sx tokens).
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', mode === 'dark' ? '#0E0F0C' : '#F4F2EC');
    document.documentElement.style.colorScheme = mode;
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const ctx = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

// Convenience hook for components that need raw colour ramps (chart axes etc.)
// — returns the mode-correct subset of `theme.js`'s `ramps` plus shared brand
// constants. Components that can use MUI's `useTheme()` should prefer that.
export function useThemeColors() {
  const { mode } = useThemeMode();
  return useMemo(() => ({
    mode,
    ...ramps[mode],
    ACCENT, ACCENT_LIGHT, ACCENT_DARK, SECONDARY, SUCCESS, WARN, DANGER,
    SIGNAL, STONE, INK, CREAM,
  }), [mode]);
}
